import * as Arrows from "../game/arrows.ts";
import type * as Dig from "../game/dig.ts";
import * as Song from "../game/song.ts";
import * as DigRender from "../dig/render.ts";
import * as Routine from "../dig/routine.ts";
import * as Sprites from "../dig/sprites.ts";
import type * as Session from "./session.ts";
import * as Site from "./site.ts";

export type Size = { readonly width: number; readonly height: number };

// What to paint, in order, in canvas pixels. The boundary executes these; nothing here touches
// a canvas, so a frame is a value a test can read. An arrow is `size` across, centred on (x, y).
export type Op =
  | { readonly kind: "clear"; readonly color: string }
  | {
      readonly kind: "rect";
      readonly x: number;
      readonly y: number;
      readonly width: number;
      readonly height: number;
      readonly color: string;
    }
  | {
      readonly kind: "arrow";
      readonly x: number;
      readonly y: number;
      readonly size: number;
      readonly direction: Arrows.Direction;
      readonly color: string;
    }
  | {
      readonly kind: "text";
      readonly text: string;
      readonly x: number;
      readonly y: number;
      readonly size: number;
      readonly color: string;
      readonly align: "left" | "center" | "right";
    }
  | {
      readonly kind: "sprite";
      readonly sprite: Sprites.Sprite;
      readonly x: number;
      readonly y: number;
      readonly scale: number;
      readonly ghost?: boolean;
    };

// What a frame is made from: the session's view, the chart the page fetched, this machine's
// clock, and whether the page may make sound yet.
export type Scene = {
  readonly view: Session.View;
  readonly chart: Arrows.Chart;
  readonly clientNow: number;
  readonly audible: boolean;
};

type Rgb = readonly [number, number, number];

const BACKGROUND = "#0f1115";
const INK = "#e6e6e6";
const INK_RGB: Rgb = [230, 230, 230];
const DIM = "#8a8f98";
const LANE = "#171b22";
// An arrow on a beat, and one on the eighth between two beats.
const BEAT_RGB: Rgb = [255, 93, 115];
const EIGHTH_RGB: Rgb = [76, 201, 240];

// The stage, in world units, scaled to fit the canvas and centred: the highway of four lanes down
// the left, the dig filling the right. One cell of the dig is one sprite.
const DIG_LEFT = 320;
const DIG_STAGE: Size = {
  width: Routine.COLUMNS * Sprites.SIZE,
  height: Routine.VIEW_ROWS * Sprites.SIZE,
};
const STAGE: Size = { width: DIG_LEFT + DIG_STAGE.width, height: DIG_STAGE.height };
const HIGHWAY = { x: 40, y: 40, width: 240, height: 400 };
const LANE_WIDTH = HIGHWAY.width / Arrows.DIRECTIONS.length;
const LANE_GAP = 4;
const ARROW = 40;
// Arrows rise from the foot of the highway to the receptors near its top, taking one second, and
// keep rising as they fade for 150 ms after. The receptors dim through each beat by this much.
const RECEPTOR_Y = 80;
const FOOT_Y = HIGHWAY.y + HIGHWAY.height;
const APPROACH_MS = 1_000;
const TAIL_MS = 150;
const PULSE = 0.6;

// The request to click sits over the highway, which the arrows cross but the words do not follow.
const HINT_X = HIGHWAY.x + HIGHWAY.width / 2;
const HINT_Y = 245;
const HINT_SIZE = 16;
const LINE_Y = 464;
const STATUS_Y = 480;
const LINE_SIZE = 12;

// A name rides just above its digger's head, on a dark plate so it reads on sky and dirt alike.
// Its width is a guess from the letters: the ops carry no font to measure with.
const NAME_SIZE = 14;
const NAME_GAP = 6;
const PLATE = "rgba(15, 17, 21, 0.6)";

// The lobby's menu sits over the dirt, below the grass, so everyone standing there stays in view.
const MENU = { x: DIG_LEFT + 360, y: 250, width: 880, height: 370 };
const MENU_MIDDLE = MENU.x + MENU.width / 2;
const MENU_COLOR = "rgba(15, 17, 21, 0.82)";
const READY = "#7bd88f";
const NEWS = "#f9d94a";
const ICON_PITCH = 100;
const ICON_BOTTOM = 430;

const rgba = (rgb: Rgb, alpha: number): string =>
  `rgba(${String(rgb[0])}, ${String(rgb[1])}, ${String(rgb[2])}, ${String(alpha)})`;

const status = (view: Session.View): string =>
  view.samples === 0
    ? "syncing clock…"
    : `offset ${view.offset < 0 ? "-" : "+"}${Math.abs(view.offset).toFixed(1)} ms · rtt ${view.rtt.toFixed(0)} ms · ${String(view.samples)} samples`;

const count = (players: number): string =>
  `${String(players)} ${players === 1 ? "player" : "players"} in the lobby`;

// What stands under someone's icon: their name once they have given one, and until then "you" on
// your own screen and "joining…" on everyone else's.
const called = (player: Dig.Player, you: number): string => {
  if (player.name !== "") {
    return player.name;
  }
  return player.slot === you ? "you" : "joining…";
};

// Keeps what lies across the dig's edge from spilling onto the highway: a piece of the dig is
// drawn only if it is all inside the dig side to side and some of it inside top to bottom.
const inside = (x: number, y: number, width: number, height: number): boolean =>
  x >= 0 && x + width <= DIG_STAGE.width && y < DIG_STAGE.height && y + height > 0;

// Anything whose place depends on time (the arrows, the pulse, where the song is, a swing) is
// drawn only once the clock is synced: before that its place would be this machine's guess, not
// everyone's. Until the page may make sound, the frame asks for the click that unlocks it. Until
// you are in a dig that has begun, the arrows, the pulse and where the song is wait with the song,
// the receptors stand dim, and the menu is drawn over everything else.
export const ops = (size: Size, scene: Scene): ReadonlyArray<Op> => {
  const scale = Math.min(size.width / STAGE.width, size.height / STAGE.height);
  const left = (size.width - STAGE.width * scale) / 2;
  const top = (size.height - STAGE.height * scale) / 2;
  const rect = (x: number, y: number, width: number, height: number, color: string): Op => ({
    kind: "rect",
    x: left + x * scale,
    y: top + y * scale,
    width: width * scale,
    height: height * scale,
    color,
  });
  const arrow = (lane: number, y: number, color: string): Op => ({
    kind: "arrow",
    x: left + (HIGHWAY.x + (lane + 0.5) * LANE_WIDTH) * scale,
    y: top + y * scale,
    size: ARROW * scale,
    direction: Arrows.DIRECTIONS[lane],
    color,
  });
  const text = (
    content: string,
    x: number,
    y: number,
    fontSize: number,
    color: string,
    align: "left" | "center" | "right",
  ): Op => ({
    kind: "text",
    text: content,
    x: left + x * scale,
    y: top + y * scale,
    size: fontSize * scale,
    color,
    align,
  });
  const sprite = (
    painted: Sprites.Sprite,
    x: number,
    y: number,
    magnify: number,
    ghost: boolean,
  ): Op => ({
    kind: "sprite",
    sprite: painted,
    x: left + x * scale,
    y: top + y * scale,
    scale: magnify * scale,
    ...(ghost ? { ghost: true } : {}),
  });

  const out: Array<Op> = [{ kind: "clear", color: BACKGROUND }];
  for (let lane = 0; lane < Arrows.DIRECTIONS.length; lane++) {
    out.push(
      rect(
        HIGHWAY.x + lane * LANE_WIDTH + LANE_GAP / 2,
        HIGHWAY.y,
        LANE_WIDTH - LANE_GAP,
        HIGHWAY.height,
        LANE,
      ),
    );
  }

  const { view } = scene;
  const { world } = view;
  const you = world.players.find((player) => player.slot === view.you);
  const digging = you !== undefined && you.ready && world.started;
  const serverNow = view.samples === 0 ? 0 : scene.clientNow + view.offset;
  const dug = Site.sceneOf(view, scene.clientNow);
  for (const op of DigRender.ops(DIG_STAGE, dug)) {
    switch (op.kind) {
      case "clear":
        out.push(rect(DIG_LEFT, 0, DIG_STAGE.width, DIG_STAGE.height, op.color));
        break;
      case "sprite":
        if (inside(op.x, op.y, Sprites.SIZE * op.scale, Sprites.SIZE * op.scale)) {
          out.push(sprite(op.sprite, DIG_LEFT + op.x, op.y, op.scale, op.ghost === true));
        }
        break;
      case "rect":
        if (inside(op.x, op.y, op.width, op.height)) {
          out.push(rect(DIG_LEFT + op.x, op.y, op.width, op.height, op.color));
        }
        break;
      default:
        return op satisfies never;
    }
  }
  for (const digger of dug.diggers) {
    const name = view.world.players.find((player) => player.slot === digger.slot)?.name ?? "";
    const x = (digger.column - dug.originColumn) * Sprites.SIZE;
    const head = (digger.row - dug.camera) * Sprites.SIZE;
    const y = head - NAME_GAP;
    if (name === "" || !inside(x, head, Sprites.SIZE, Sprites.SIZE) || y < NAME_SIZE) {
      continue;
    }
    const width = name.length * NAME_SIZE * 0.6 + NAME_GAP * 2;
    const middle = DIG_LEFT + x + Sprites.SIZE / 2;
    out.push(rect(middle - width / 2, y - NAME_SIZE, width, NAME_SIZE + NAME_GAP, PLATE));
    out.push(text(name, middle, y, NAME_SIZE, INK, "center"));
  }
  if (view.samples === 0 || !digging) {
    for (let lane = 0; lane < Arrows.DIRECTIONS.length; lane++) {
      out.push(arrow(lane, RECEPTOR_Y, DIM));
    }
  } else {
    const place = Song.placeAt(Song.SONG, serverNow);
    for (let lane = 0; lane < Arrows.DIRECTIONS.length; lane++) {
      out.push(arrow(lane, RECEPTOR_Y, rgba(INK_RGB, 1 - PULSE * place.phase)));
    }
    for (const coming of Arrows.arrowsBetween(
      scene.chart,
      Song.barMs(Song.SONG),
      serverNow - TAIL_MS,
      serverNow + APPROACH_MS,
    )) {
      const until = coming.at - serverNow;
      out.push(
        arrow(
          coming.lane,
          RECEPTOR_Y + (until / APPROACH_MS) * (FOOT_Y - RECEPTOR_Y),
          rgba(coming.onBeat ? BEAT_RGB : EIGHTH_RGB, Math.min(1, 1 + until / TAIL_MS)),
        ),
      );
    }
    out.push(
      text(
        `${place.section} · bar ${String(place.bar + 1)} · beat ${String(place.beat + 1)}`,
        HIGHWAY.x,
        LINE_Y,
        LINE_SIZE,
        DIM,
        "left",
      ),
    );
  }
  if (!scene.audible) {
    out.push(text("click anywhere for sound", HINT_X, HINT_Y, HINT_SIZE, INK, "center"));
  }
  out.push(text(status(view), HIGHWAY.x, STATUS_Y, LINE_SIZE, DIM, "left"));

  if (you === undefined || digging) {
    return out;
  }
  out.push(rect(MENU.x, MENU.y, MENU.width, MENU.height, MENU_COLOR));
  out.push(text("Ready up", MENU_MIDDLE, 300, 30, INK, "center"));
  out.push(text(count(world.players.length), MENU_MIDDLE, 330, 14, DIM, "center"));
  // A crowd too wide for the menu at full size is drawn smaller, so every icon still fits.
  const pitch = Math.min(ICON_PITCH, (MENU.width - 40) / world.players.length);
  const iconScale = pitch / ICON_PITCH;
  const first = MENU_MIDDLE - (pitch * world.players.length) / 2;
  world.players.forEach((player, index) => {
    const frames = Sprites.DIGGERS[Sprites.CHARACTERS[player.slot % Sprites.CHARACTERS.length]];
    const body = player.ready ? frames.ready[0] : frames.wait[0];
    const mine = player.slot === view.you;
    const middle = first + (index + 0.5) * pitch;
    const across = Sprites.SIZE * iconScale;
    out.push(
      sprite(
        mine ? body : Sprites.ghost(body),
        middle - across / 2,
        ICON_BOTTOM - across,
        iconScale,
        !mine,
      ),
    );
    out.push(text(called(player, view.you), middle, 448, 13, INK, "center"));
    out.push(
      text(
        player.ready ? "ready" : "not ready",
        middle,
        464,
        11,
        player.ready ? READY : DIM,
        "center",
      ),
    );
  });
  if (you.ready) {
    out.push(text("Waiting for everyone to ready up", MENU_MIDDLE, 520, 18, INK, "center"));
  } else {
    out.push(text(`Name: ${view.draft}_`, MENU_MIDDLE, 515, 20, INK, "center"));
    out.push(
      text(
        view.draft.trim() === "" ? "Type your name to ready up" : "Press Enter to ready up",
        MENU_MIDDLE,
        545,
        13,
        DIM,
        "center",
      ),
    );
  }
  if (world.started && !you.ready) {
    out.push(text("The dig has begun: ready up to join it", MENU_MIDDLE, 590, 13, NEWS, "center"));
  }
  return out;
};
